import React, { useState, useRef } from "react";
import Helmet from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { reduxForm } from "redux-form";

//imported
import { loginCheck, loginUser } from "store/actions";
import * as Path from "routes/Path/index";

import { FORGOT_PASSWORD, LOGIN } from "common/viewContent";
import FormikForm from "components/shared/FormikForm";
import { initialValidationSchema } from "./Validation";
import { initialInputFields } from "./inputFields";
import { toast } from "react-toastify";

const OTP_LENGTH = 6;
const BACKUP_CODE_LENGTH = 6;

const Login = ({ submitting }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { loading } = useSelector((state) => state.Auth);

  // "credentials" | "2fa" | "backup"
  const [step, setStep] = useState("credentials");

  // credentials from step 1
  const [formValues, setFormValues] = useState({
    email: "",
    password: "",
  });

  // OTP digits for step 2
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const otpRefs = useRef([]);

  // backup code digits for step 3
  const [backupCode, setBackupCode] = useState(Array(BACKUP_CODE_LENGTH).fill(""));
  const backupRefs = useRef([]);

  const isOtpComplete = otp.every((digit) => digit !== "");
  const isBackupCodeComplete = backupCode.every((digit) => digit !== "");

  const boxStyle = {
    width: "48px",
    height: "48px",
    fontSize: "20px",
    fontWeight: "bold",
    margin: "0 4px",
    border: "1px solid #ced4da",
    borderRadius: "6px",
    backgroundColor: "#fff",
    color: "#212529",
  };

  const handleOtpChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const newOtp = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((char, i) => (newOtp[i] = char));
    setOtp(newOtp);
    otpRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleBackupCodeChange = (value, index) => {
    if (!/^[0-9a-zA-Z]?$/.test(value)) return;
    const newCode = [...backupCode];
    newCode[index] = value;
    setBackupCode(newCode);

    if (value && index < BACKUP_CODE_LENGTH - 1) {
      backupRefs.current[index + 1]?.focus();
    }
  };

  const handleBackupCodeKeyDown = (e, index) => {
    if (e.key === "Backspace" && !backupCode[index] && index > 0) {
      backupRefs.current[index - 1]?.focus();
    }
  };

  const handleBackupCodePaste = (e) => {
    const pasted = e.clipboardData
      .getData("text")
      .replace(/[^0-9a-zA-Z]/g, "")
      .slice(0, BACKUP_CODE_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const newCode = Array(BACKUP_CODE_LENGTH).fill("");
    pasted.split("").forEach((char, i) => (newCode[i] = char));
    setBackupCode(newCode);
    backupRefs.current[Math.min(pasted.length, BACKUP_CODE_LENGTH - 1)]?.focus();
  };

  const handleLoginCheck = async (values) => {
    try {
      const callBack = (err, response) => {
        if (err) return;

        const is2FAEnabled =
          response?.data === true ||
          response?.data?.is2FA === true ||
          response?.data?.twoFactorStatus === true;

        setFormValues({ email: values.email, password: values.password });

        if (is2FAEnabled) {
          setOtp(Array(OTP_LENGTH).fill(""));
          setStep("2fa");
        } else {
          // twoFactorStatus is false: user hasn't set up 2FA yet, send them to setup
          navigate('/twofa_setup', {
            state: { email: values.email, password: values.password },
          });
        }
      };
      await dispatch(
        loginCheck({
          data: { email: values.email, password: values.password },
          callBack,
        })
      );
    } catch (err) {
      console.error(err, "<====err");
      toast.error(err.message);
    }
  };

  const handleLoginSuccessRedirect = (response) => {
    if (response?.status === "success" && response?.status_code === 200) {
      setTimeout(() => {
        navigate("/admin/" + Path.profile);
        return;
      }, 1500);
    }
  };

  const handleLoginUser = async () => {
    if (!isOtpComplete) return;
    try {
      const callBack = (err, response) => handleLoginSuccessRedirect(response);
      const payload = {
        email: formValues.email,
        password: formValues.password,
        twoFACode: otp.join(""),
      };
      await dispatch(loginUser({ data: payload, callBack }));
    } catch (err) {
      console.error(err, "<====err");
      toast.error(err.message);
    }
  };

  const handleLoginWithBackupCode = async () => {
    if (!isBackupCodeComplete) return;
    try {
      const callBack = (err, response) => handleLoginSuccessRedirect(response);
      const payload = {
        email: formValues.email,
        password: formValues.password,
        backupCode: backupCode.join(""),
      };
      await dispatch(loginUser({ data: payload, callBack }));
    } catch (err) {
      console.error(err, "<====err");
      toast.error(err.message);
    }
  };

  const goToBackupCode = () => {
    setBackupCode(Array(BACKUP_CODE_LENGTH).fill(""));
    setStep("backup");
  };

  const goBackTo2FA = () => {
    setStep("2fa");
  };

  return (
    <>
      <Helmet title={LOGIN.CURRENT_MODULE} />
      <style>{`
        .login-back-btn {
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          border: 1px solid #e0e0e0;
          background-color: #f8f9fa;
          color: #495057;
          transition: background-color 0.15s ease, border-color 0.15s ease;
        }
        .login-back-btn:hover {
          background-color: #eef0f2;
          border-color: #ced4da;
        }
        .login-back-btn:active {
          background-color: #e2e6ea;
        }
      `}</style>
      <div className="container mt-5" style={{ overflow: "hidden" }}>
        <div className="row">
          <div className="col-12 col-sm-8 offset-sm-2 col-md-6 offset-md-3 col-lg-6 offset-lg-3 col-xl-4 offset-xl-4">
            <div className="login-brand text-center mb-4">
              <h2 className="mb-1">Welcome Back</h2>
              <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                Bitanica Games
              </p>
            </div>

            <div className="card card-primary">
              <div
                className="card-header"
                style={{
                  display: "grid",
                  gridTemplateColumns: "40px 1fr 40px",
                  alignItems: "center",
                }}
              >
                <div>
                  {step === "backup" && (
                    <button
                      type="button"
                      onClick={goBackTo2FA}
                      className="login-back-btn"
                      aria-label="Back"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="19" y1="12" x2="5" y2="12" />
                        <polyline points="12 19 5 12 12 5" />
                      </svg>
                    </button>
                  )}
                </div>
                <h4 className="mb-0 text-center">Login to Account</h4>
                <div />
              </div>

              <div className="card-body">
                {step === "credentials" && (
                  <>
                    <FormikForm
                      initialValues={formValues}
                      validationSchema={initialValidationSchema}
                      onSubmit={handleLoginCheck}
                      loading={loading}
                      inputFields={initialInputFields}
                      submitting={submitting}
                      buttonText="Continue"
                      is_block={true}
                      buttonCol={12}
                      col={12}
                      is_passwordHide={false}
                    />
                    <div className="mt-3 text-center">
                      <Link
                        to={`/${Path.forgot_password}`.replace(/^\/+/, "/")}
                        className="text-small text-muted"
                        style={{ textDecoration: "none" }}
                      >
                        Not Remember Password?
                      </Link>
                    </div>
                  </>
                )}

                {step === "2fa" && (
                  <>
                    <p className="mb-3 text-center">
                      Enter the 6-digit code from your authenticator app
                    </p>
                    <div
                      className="d-flex justify-content-between mb-4"
                      onPaste={handleOtpPaste}
                    >
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={(el) => (otpRefs.current[index] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) =>
                            handleOtpChange(e.target.value, index)
                          }
                          onKeyDown={(e) => handleOtpKeyDown(e, index)}
                          className="text-center"
                          style={boxStyle}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary btn-block w-100"
                      disabled={!isOtpComplete || loading}
                      onClick={handleLoginUser}
                    >
                      Login
                    </button>
                    <div className="mt-3 text-center">
                      <button
                        type="button"
                        onClick={goToBackupCode}
                        className="btn btn-link p-0 text-small text-muted"
                        style={{ textDecoration: "none" }}
                      >
                        Login with Backup Code
                      </button>
                    </div>
                  </>
                )}

                {step === "backup" && (
                  <>
                    <p className="mb-3 text-center">
                      Please enter your backup code
                    </p>
                    <div
                      className="d-flex justify-content-between mb-4"
                      onPaste={handleBackupCodePaste}
                    >
                      {backupCode.map((digit, index) => (
                        <input
                          key={index}
                          ref={(el) => (backupRefs.current[index] = el)}
                          type="text"
                          maxLength={1}
                          value={digit}
                          onChange={(e) =>
                            handleBackupCodeChange(e.target.value, index)
                          }
                          onKeyDown={(e) => handleBackupCodeKeyDown(e, index)}
                          className="text-center"
                          style={boxStyle}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary btn-block w-100"
                      disabled={!isBackupCodeComplete || loading}
                      onClick={handleLoginWithBackupCode}
                    >
                      Login
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="simple-footer text-center mt-4">
              Copyright &copy; 2024 Bitanica Games. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const LoginForm = reduxForm({
  form: "login",
})(Login);

export default LoginForm;